import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * /coupons — list + create. The active flag is a live-toggle Switch in the
 * table (PATCH per click). Expiry shows the date + a red "פג תוקף" tag when
 * past; the row itself dims to make expiration visible at a glance.
 *
 * Create form:
 *  - code (required, unique — duplicate → 409 with a friendly Hebrew msg)
 *  - discount_percent — the four approved tiers
 *  - kind — single_use or general; when single_use, the max_redemptions
 *    field is hidden (server also enforces NULL on that column, so the
 *    hide is UX only, not a trust boundary)
 *  - expires_at — DatePicker with time; empty = never expires
 *  - active — Switch, defaults on
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, DatePicker, Empty, Form, Input, InputNumber, Modal, Popconfirm, Select, Spin, Switch, Table, Tag, Typography, } from 'antd';
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { createCoupon, deleteCoupon, getCouponRedemptions, listCoupons, updateCoupon, } from '../api';
import { PageHeader } from '../components/PageHeader';
import { notifySuccess } from '../components/errors';
import { formatAgorotHe, formatHebrewDate, formatRelativeTimeHe } from './dashboard/helpers';
const { Text } = Typography;
export default function CouponsScreen() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    // The coupon whose redemption-events modal is currently open, or null.
    const [redemptionsFor, setRedemptionsFor] = useState(null);
    // Per-row "active toggle busy" flags so the switch shows a spinner without
    // blocking the whole table. Keyed by coupon id.
    const [togglingIds, setTogglingIds] = useState(new Set());
    const reload = useCallback(async () => {
        setLoading(true);
        try {
            setRows(await listCoupons());
        }
        catch { /* client auto-surfaces */ }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { void reload(); }, [reload]);
    const toggleActive = async (row, next) => {
        setTogglingIds((s) => { const n = new Set(s); n.add(row.id); return n; });
        // Optimistic update: flip immediately so the toggle feels instant;
        // rollback on error. Matches the "no dead clicks" rule from CLAUDE.md.
        setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, active: next } : r));
        try {
            await updateCoupon(row.id, { active: next });
            notifySuccess(next ? 'הקופון הופעל' : 'הקופון הושבת');
        }
        catch {
            // Rollback the optimistic flip. The toast is handled by the central
            // pipeline (setErrorReporter) — no local notifyError needed.
            setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, active: !next } : r));
        }
        finally {
            setTogglingIds((s) => { const n = new Set(s); n.delete(row.id); return n; });
        }
    };
    const cols = [
        { title: 'קוד', dataIndex: 'code', render: (v) => _jsx(Text, { code: true, children: v }) },
        { title: 'הנחה', dataIndex: 'discount_percent', width: 90, render: (v) => `${v}%` },
        {
            title: 'סוג', dataIndex: 'kind', width: 110,
            render: (v) => _jsx(Tag, { children: v === 'single_use' ? 'חד-פעמי' : 'כללי' }),
        },
        {
            title: 'מימושים', key: '__red', width: 110,
            render: (_, c) => {
                const text = c.kind === 'single_use'
                    ? `${c.times_redeemed} / 1`
                    : `${c.times_redeemed} / ${c.max_redemptions ?? '∞'}`;
                // Only clickable when there's at least one redemption to show —
                // zero-count cells stay as plain text (empty-state not needed per spec).
                if (c.times_redeemed <= 0)
                    return _jsx(Text, { children: text });
                return (_jsx(Typography.Link, { onClick: () => setRedemptionsFor(c), children: text }));
            },
        },
        {
            title: 'תוקף', key: '__exp', width: 220,
            render: (_, c) => _jsx(ExpiryCell, { expires: c.expires_at }),
        },
        {
            title: 'פעיל', key: '__active', width: 90,
            render: (_, c) => (_jsx(Switch, { checked: c.active, loading: togglingIds.has(c.id), onChange: (next) => toggleActive(c, next) })),
        },
        {
            title: 'פעולות', width: 80, fixed: 'right',
            render: (_, c) => (_jsx(Popconfirm, { title: "\u05DC\u05DE\u05D7\u05D5\u05E7 \u05E7\u05D5\u05E4\u05D5\u05DF?", okText: "\u05DE\u05D7\u05E7", cancelText: "\u05D1\u05D8\u05DC", onConfirm: async () => {
                    try {
                        await deleteCoupon(c.id);
                        notifySuccess('קופון נמחק');
                        reload();
                    }
                    catch { /* client auto-surfaces */ }
                }, children: _jsx(Button, { size: "small", danger: true, icon: _jsx(DeleteOutlined, {}) }) })),
        },
    ];
    return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: "\u05E7\u05D5\u05E4\u05D5\u05E0\u05D9\u05DD", breadcrumb: [{ label: 'ניהול' }, 'קופונים'], actions: _jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: () => setCreateOpen(true), children: "\u05E7\u05D5\u05E4\u05D5\u05DF \u05D7\u05D3\u05E9" }) }), _jsx(Card, { children: rows.length === 0 && !loading
                    ? (_jsx(Empty, { description: "\u05D0\u05D9\u05DF \u05E2\u05D3\u05D9\u05D9\u05DF \u05E7\u05D5\u05E4\u05D5\u05E0\u05D9\u05DD", image: Empty.PRESENTED_IMAGE_SIMPLE, children: _jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: () => setCreateOpen(true), children: "\u05E7\u05D5\u05E4\u05D5\u05DF \u05D7\u05D3\u05E9" }) }))
                    : (_jsx(Table, { size: "small", rowKey: "id", pagination: { pageSize: 50 }, loading: loading, dataSource: rows, columns: cols, 
                        // Dim expired-or-disabled rows so an inactive coupon reads as
                        // "not in effect" at a glance without needing to compare the
                        // toggle + the tag manually.
                        rowClassName: (r) => (!r.active || isExpired(r.expires_at)) ? 'admin-coupon-inactive' : '' })) }), _jsx("style", { children: `.admin-coupon-inactive { opacity: 0.55 }` }), _jsx(CouponCreateModal, { open: createOpen, onClose: () => setCreateOpen(false), onDone: () => { setCreateOpen(false); reload(); } }), _jsx(RedemptionsModal, { coupon: redemptionsFor, onClose: () => setRedemptionsFor(null) })] }));
}
// ---------------------------------------------------------------------------
// Redemptions modal — one row per redemption event. Student links to the
// per-student page; grade and amount come straight off the join in
// GET /admin/coupons/:id/redemptions.
// ---------------------------------------------------------------------------
function RedemptionsModal({ coupon, onClose }) {
    const [state, setState] = useState({ kind: 'idle' });
    const load = useCallback(async (id) => {
        setState({ kind: 'loading' });
        try {
            const page = await getCouponRedemptions(id);
            setState({ kind: 'ready', items: page.items });
        }
        catch (err) {
            setState({ kind: 'error', message: err.message || 'שגיאה בטעינה' });
        }
    }, []);
    useEffect(() => {
        if (coupon)
            void load(coupon.id);
        else
            setState({ kind: 'idle' });
    }, [coupon, load]);
    const cols = [
        {
            title: 'תאריך', dataIndex: 'redeemed_at', width: 220,
            render: (v) => {
                if (!v)
                    return _jsx(Text, { type: "secondary", children: "\u2014" });
                const d = new Date(v);
                const abs = Number.isFinite(d.getTime()) ? formatHebrewDate(d) : v;
                return (_jsxs("span", { children: [_jsx(Text, { children: formatRelativeTimeHe(v) }), _jsxs(Text, { type: "secondary", style: { fontSize: 12, marginInlineStart: 8 }, children: ["(", abs, ")"] })] }));
            },
        },
        {
            title: 'תלמיד/ה', key: '__stu',
            render: (_, r) => (_jsx(Link, { to: `/students/${r.student.id}`, onClick: onClose, children: r.student.display_name?.trim() || r.student.email })),
        },
        { title: 'כיתה', dataIndex: 'grade_name', width: 180 },
        {
            title: 'סכום (₪)', dataIndex: 'amount_paid', width: 110,
            render: (v) => formatAgorotHe(v),
        },
    ];
    const titleCode = coupon ? ` · ${coupon.code}` : '';
    return (_jsxs(Modal, { title: `מימושים של הקופון${titleCode}`, open: coupon !== null, onCancel: onClose, footer: null, width: 720, destroyOnHidden: true, children: [state.kind === 'loading' && (_jsx("div", { style: { display: 'flex', justifyContent: 'center', padding: 24 }, children: _jsx(Spin, {}) })), state.kind === 'error' && (_jsx(Alert, { type: "error", message: "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D8\u05E2\u05D9\u05E0\u05EA \u05D4\u05DE\u05D9\u05DE\u05D5\u05E9\u05D9\u05DD", description: state.message, action: _jsx(Button, { size: "small", icon: _jsx(ReloadOutlined, {}), onClick: () => coupon && void load(coupon.id), children: "\u05E0\u05E1\u05D4 \u05E9\u05D5\u05D1" }), showIcon: true })), state.kind === 'ready' && (_jsx(Table, { size: "small", rowKey: "id", pagination: false, dataSource: state.items, columns: cols }))] }));
}
function ExpiryCell({ expires }) {
    if (!expires)
        return _jsx(Text, { type: "secondary", children: "\u05DC\u05DC\u05D0 \u05EA\u05E4\u05D5\u05D2\u05D4" });
    const formatted = dayjs(expires).format('DD/MM/YYYY HH:mm');
    const expired = isExpired(expires);
    return (_jsxs("span", { children: [formatted, ' ', expired && _jsx(Tag, { color: "red", children: "\u05E4\u05D2 \u05EA\u05D5\u05E7\u05E3" })] }));
}
function isExpired(expires) {
    if (!expires)
        return false;
    return dayjs(expires).isBefore(dayjs());
}
function CouponCreateModal({ open, onClose, onDone }) {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    // Watch kind so we can hide max_redemptions for single_use (server also
    // forces NULL there; this is UX-only to reduce cognitive load in the form).
    const kind = Form.useWatch('kind', form) ?? 'general';
    const submit = async () => {
        let v;
        try {
            v = await form.validateFields();
        }
        catch {
            return;
        }
        setSubmitting(true);
        try {
            const expiresAt = v.expires_at;
            await createCoupon({
                code: v.code.trim(),
                discount_percent: Number(v.discount_percent),
                kind: v.kind,
                max_redemptions: v.kind === 'single_use' ? null : (v.max_redemptions ?? null),
                expires_at: expiresAt ? expiresAt.toISOString() : null,
                active: v.active !== false,
            });
            notifySuccess('קופון נוצר');
            form.resetFields();
            onDone();
        }
        catch { /* client auto-surfaces */ }
        finally {
            setSubmitting(false);
        }
    };
    return (_jsx(Modal, { title: "\u05D9\u05E6\u05D9\u05E8\u05EA \u05E7\u05D5\u05E4\u05D5\u05DF", open: open, onCancel: onClose, onOk: submit, okText: "\u05E6\u05D5\u05E8", cancelText: "\u05D1\u05D8\u05DC", confirmLoading: submitting, destroyOnHidden: true, children: _jsxs(Form, { form: form, layout: "vertical", preserve: false, initialValues: { discount_percent: 30, kind: 'general', active: true }, children: [_jsx(Form.Item, { name: "code", label: "\u05E7\u05D5\u05D3", rules: [{ required: true, message: 'קוד נדרש' }], children: _jsx(Input, { placeholder: "\u05DC\u05DE\u05E9\u05DC: LAUNCH30" }) }), _jsx(Form.Item, { name: "discount_percent", label: "\u05D4\u05E0\u05D7\u05D4", tooltip: "\u05E8\u05E7 \u05D4\u05DE\u05D3\u05E8\u05D2\u05D5\u05EA \u05D4\u05DE\u05D0\u05D5\u05E9\u05E8\u05D5\u05EA: 20/30/40/100%", children: _jsx(Select, { options: [20, 30, 40, 100].map((v) => ({ value: v, label: `${v}%` })) }) }), _jsx(Form.Item, { name: "kind", label: "\u05E1\u05D5\u05D2", children: _jsx(Select, { options: [
                            { value: 'single_use', label: 'חד-פעמי' },
                            { value: 'general', label: 'כללי (עם מקסימום מימושים)' },
                        ] }) }), kind === 'general' && (_jsx(Form.Item, { name: "max_redemptions", label: "\u05DE\u05E7\u05E1\u05D9\u05DE\u05D5\u05DD \u05DE\u05D9\u05DE\u05D5\u05E9\u05D9\u05DD", tooltip: "\u05E8\u05D9\u05E7 = \u05DC\u05DC\u05D0 \u05D4\u05D2\u05D1\u05DC\u05D4", children: _jsx(InputNumber, { min: 1, style: { width: '100%' }, placeholder: "\u05DC\u05DC\u05D0 \u05D4\u05D2\u05D1\u05DC\u05D4" }) })), _jsx(Form.Item, { name: "expires_at", label: "\u05EA\u05D5\u05E7\u05E3", tooltip: "\u05E8\u05D9\u05E7 = \u05DC\u05DC\u05D0 \u05EA\u05E4\u05D5\u05D2\u05D4", children: _jsx(DatePicker, { showTime: { format: 'HH:mm' }, format: "DD/MM/YYYY HH:mm", style: { width: '100%' }, placeholder: "\u05D1\u05D7\u05E8 \u05EA\u05D0\u05E8\u05D9\u05DA \u05D5\u05E9\u05E2\u05D4" }) }), _jsx(Form.Item, { name: "active", label: "\u05E4\u05E2\u05D9\u05DC", valuePropName: "checked", children: _jsx(Switch, {}) })] }) }));
}
